import {
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    BarChart as BarChartIcon,
    VisibilityOff as VisibilityOffIcon,
    Add as ZoomInIcon,
    Remove as ZoomOutIcon,
    MyLocation as MyLocationIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
    Snackbar,
    Alert,
} from "@mui/material";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { colors } from "components/Theme/WebTheme";
import {
    useEffect,
    useState,
    useMemo,
    useCallback,
    lazy,
    Suspense,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardCard from "../DashboardCard";
import { useDispatch } from "react-redux";
import { api } from "common";

// Lazy load Chart component untuk mengurangi initial bundle size
const Chart = lazy(() => import("react-apexcharts"));

// Import FirebaseRealtime di top level (bukan di dalam loop)
let FirebaseRealtime = null;
const loadFirebaseRealtime = async () => {
    if (!FirebaseRealtime) {
        const module = await import("../../utils/firebaseRealtime.js");
        FirebaseRealtime = module.default;
    }
    return FirebaseRealtime;
};

// Throttle utility untuk location updates (membatasi frekuensi updates)
const throttle = (func, limit) => {
    let inThrottle;
    return function executedFunction(...args) {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};

// Polyfill for process (required by Google Maps API)
if (typeof process === "undefined") {
    window.process = { env: {} };
}

const DashboardNewUI = ({
    // All props received from Dashboard parent
    customerCount,
    driverCount,
    activeCount,
    powerTranzActive,
    auth,
    mylocation,
    locationStatus,
    locations,
    activeBookings, // Add activeBookings prop
    countries,
    selectedCountry,
    handleCountrySelect,
    dailygross,
    monthlygross,
    totalgross,
    data,
    onRefresh,
    barChartOptions,
    barChart,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getBookingData } = api;

    // Helper function to show notifications - memoized to prevent re-renders
    const showNotification = useCallback((message, severity = "info") => {
        setNotification({
            open: true,
            message,
            severity,
        });
    }, []);

    // State untuk menyimpan real-time locations dari multiple bookings
    const [realTimeLocations, setRealTimeLocations] = useState(new Map());

    // State untuk menyimpan real-time status dari multiple bookings
    const [realTimeBookingStatuses, setRealTimeBookingStatuses] = useState(
        new Map()
    );

    // Throttle location updates untuk mengurangi re-renders yang berlebihan
    // Location updates bisa terjadi sangat sering (every few seconds)
    const throttledLocationUpdateRef = useRef(
        throttle((bookingId, locationData) => {
            setRealTimeLocations((prev) => {
                const updated = new Map(prev);
                updated.set(bookingId, locationData);
                return updated;
            });
        }, 1000) // Update max 1x per detik
    );

    const throttledLocationUpdate = useCallback((bookingId, locationData) => {
        throttledLocationUpdateRef.current(bookingId, locationData);
    }, []);

    // State untuk highlight mode - harus sebelum useEffect yang memakainya
    const [highlightOngoingTrips, setHighlightOngoingTrips] = useState(false);

    // Custom effect untuk tracking multiple ongoing trips - OPTIMIZED
    // HANYA AKTIF ketika card ongoing trips di-highlight (diklik)
    useEffect(() => {
        const unsubscribeFunctions = [];

        // Track setiap ongoing booking HANYA jika highlight aktif
        const ongoingBookings =
            activeBookings?.filter((booking) =>
                ["ACCEPTED", "ARRIVED", "STARTED", "REACHED"].includes(
                    booking.status
                )
            ) || [];

        // Load Firebase Realtime HANYA ketika card di-klik (highlightOngoingTrips = true)
        if (ongoingBookings.length > 0 && highlightOngoingTrips) {
            loadFirebaseRealtime()
                .then((FirebaseRT) => {
                    ongoingBookings.forEach((booking) => {
                        // Track location - dengan throttling untuk performance
                        const locationUnsubscribe =
                            FirebaseRT.listenToDriverLocation(
                                booking.id,
                                (location) => {
                                    // Use throttled update untuk mengurangi re-renders
                                    throttledLocationUpdate(booking.id, {
                                        lat: location.lat,
                                        lng: location.lng,
                                        timestamp: location.timestamp,
                                        bookingId: booking.id,
                                    });
                                },
                                (error) => {
                                    console.error(
                                        `Error tracking booking location ${booking.id}:`,
                                        error
                                    );
                                }
                            );

                        // Track booking status changes
                        const statusUnsubscribe =
                            FirebaseRT.listenToBookingStatus(
                                booking.id,
                                (statusData) => {
                                    setRealTimeBookingStatuses((prev) => {
                                        const updated = new Map(prev);
                                        updated.set(booking.id, {
                                            status: statusData.status,
                                            timestamp:
                                                statusData.timestamp ||
                                                Date.now(),
                                            bookingId: booking.id,
                                            previousStatus: prev.get(booking.id)
                                                ?.status,
                                            statusHistory: [
                                                ...(prev.get(booking.id)
                                                    ?.statusHistory || []),
                                                {
                                                    status: statusData.status,
                                                    timestamp:
                                                        statusData.timestamp ||
                                                        Date.now(),
                                                },
                                            ].slice(-10), // Keep last 10 status changes
                                        });
                                        return updated;
                                    });

                                    // Show notification for status changes
                                    if (statusData.status) {
                                        // Use translation keys for multi-language support
                                        const getStatusMessage = (status) => {
                                            const statusKeys = {
                                                NEW: "status_new_created",
                                                ACCEPTED:
                                                    "status_driver_accepted",
                                                ARRIVED:
                                                    "status_driver_arrived",
                                                STARTED: "status_trip_started",
                                                REACHED:
                                                    "status_reached_destination",
                                                PAID: "status_payment_completed",
                                                CANCELLED:
                                                    "status_booking_cancelled",
                                            };

                                            return statusKeys[status]
                                                ? t(statusKeys[status])
                                                : t("status_changed_to", {
                                                      status: status,
                                                  });
                                        };

                                        const message = getStatusMessage(
                                            statusData.status
                                        );
                                        showNotification(
                                            `${message} (ID: ${
                                                booking.reference || booking.id
                                            })`,
                                            statusData.status === "CANCELLED"
                                                ? "error"
                                                : "info"
                                        );
                                    }
                                },
                                (error) => {
                                    console.error(
                                        `Error tracking booking status ${booking.id}:`,
                                        error
                                    );
                                }
                            );

                        unsubscribeFunctions.push(locationUnsubscribe);
                        unsubscribeFunctions.push(statusUnsubscribe);
                    });
                })
                .catch((error) => {
                    console.error("Failed to load Firebase Realtime:", error);
                });
        }

        // Cleanup function - akan otomatis unsubscribe ketika highlight di-off
        return () => {
            unsubscribeFunctions.forEach((unsubscribe) => {
                if (unsubscribe) unsubscribe();
            });
            // Clear state ketika listener di-stop
            if (!highlightOngoingTrips) {
                setRealTimeLocations(new Map());
                setRealTimeBookingStatuses(new Map());
            }
        };
    }, [
        activeBookings,
        t,
        showNotification,
        throttledLocationUpdate,
        highlightOngoingTrips,
    ]);

    // New UI specific state
    const [autoRefresh] = useState(true); // Auto refresh selalu aktif
    const [refreshInterval, setRefreshInterval] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    // highlightOngoingTrips sudah didefinisikan di atas sebelum useEffect
    const [showChart, setShowChart] = useState(true);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [isMapLoading, setIsMapLoading] = useState(true); // Add map loading state

    // Notification state
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "info", // success, error, warning, info
    });

    const [mapCenter, setMapCenter] = useState({
        lat: 8.983333,
        lng: -79.516667,
    });
    const [mapZoom, setMapZoom] = useState(10);

    // Zoom controls functions
    const handleZoomIn = () => {
        setMapZoom((prevZoom) => Math.min(prevZoom + 1, 20)); // Max zoom 20
    };

    const handleZoomOut = () => {
        setMapZoom((prevZoom) => Math.max(prevZoom - 1, 1)); // Min zoom 1
    };

    // Close notification
    const handleCloseNotification = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setNotification({ ...notification, open: false });
    };

    // Get current location function with fallback
    const handleGetCurrentLocation = async () => {
        if (isGettingLocation) return; // Prevent multiple requests

        setIsGettingLocation(true);

        // Try HTML5 Geolocation first
        if (navigator.geolocation) {
            // Try with high accuracy first
            const tryGeolocation = (enableHighAccuracy = true) => {
                return new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const { latitude, longitude, accuracy } =
                                position.coords;
                            resolve({
                                lat: latitude,
                                lng: longitude,
                                accuracy: accuracy,
                            });
                        },
                        (error) => {
                            console.error("Geolocation error:", error);
                            reject(error);
                        },
                        {
                            enableHighAccuracy: enableHighAccuracy,
                            timeout: enableHighAccuracy ? 15000 : 10000,
                            maximumAge: enableHighAccuracy ? 0 : 300000, // 5 minutes for low accuracy
                        }
                    );
                });
            };

            try {
                // Try high accuracy first
                let location;
                try {
                    location = await tryGeolocation(true);
                } catch (highAccuracyError) {
                    location = await tryGeolocation(false);
                }

                const newCenter = {
                    lat: location.lat,
                    lng: location.lng,
                };

                setMapCenter(newCenter);
                setMapZoom(15);

                showNotification(
                    t("location_found_with_accuracy", {
                        accuracy: Math.round(location.accuracy),
                    }),
                    "success"
                );
            } catch (error) {
                console.error("All geolocation attempts failed:", error);

                // Try IP-based location as fallback
                try {
                    await tryIPLocation();
                } catch (ipError) {
                    handleLocationError(error);
                }
            }
        } else {
            // Try IP-based location for browsers without geolocation
            try {
                await tryIPLocation();
            } catch (error) {
                showNotification(t("geolocation_not_supported"), "error");
            }
        }

        setIsGettingLocation(false);
    };

    // Fallback to IP-based location
    const tryIPLocation = async () => {
        try {
            // Using ipapi.co for IP geolocation (free service)
            const response = await fetch("https://ipapi.co/json/");
            const data = await response.json();

            if (data.latitude && data.longitude) {
                const newCenter = {
                    lat: parseFloat(data.latitude),
                    lng: parseFloat(data.longitude),
                };

                setMapCenter(newCenter);
                setMapZoom(12); // Lower zoom for IP-based location (less accurate)
                // Show info that this is approximate location
                showNotification(
                    t("location_set_ip_based", {
                        city: data.city,
                        country: data.country_name,
                    }),
                    "warning"
                );
                return;
            }
            throw new Error("No coordinates in IP location response");
        } catch (error) {
            console.error("IP location failed:", error);
            throw error;
        }
    };

    // Enhanced error handling
    const handleLocationError = (error) => {
        let message = "";

        switch (error.code) {
            case error.PERMISSION_DENIED:
                message = t("location_permission_denied");
                break;
            case error.POSITION_UNAVAILABLE:
                message = t("location_position_unavailable");
                break;
            case error.TIMEOUT:
                message = t("location_timeout");
                break;
            default:
                message = t("location_error_general");
                break;
        }

        showNotification(message, "error");
    };

    // Check if Google Maps is loaded (from App.js)
    const isLoaded = window.googleMapsStatus?.isLoaded || false;

    // Handle map loading state
    useEffect(() => {
        if (isLoaded) {
            // Delay untuk memastikan map sudah fully rendered
            const timer = setTimeout(() => {
                setIsMapLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isLoaded]);

    // Auto refresh setup
    useEffect(() => {
        if (autoRefresh && onRefresh) {
            const interval = setInterval(() => {
                onRefresh(); // Call parent refresh function
            }, 30000); // Refresh setiap 30 detik
            setRefreshInterval(interval);

            return () => {
                clearInterval(interval);
            };
        } else {
            // Clear any existing interval when auto-refresh is disabled
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }
    }, [autoRefresh, onRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update map center when mylocation changes
    useEffect(() => {
        if (mylocation && mylocation.lat && mylocation.lng) {
            setMapCenter(mylocation);
            setMapZoom(locationStatus === "fallback" ? 12 : 10);
        }
    }, [mylocation, locationStatus]);

    // Separate data sources for different marker types

    // 1. Filter ongoing bookings (active trips in progress) - memoized
    const ongoingBookings = useMemo(() => {
        return (
            activeBookings?.filter((booking) =>
                ["ACCEPTED", "ARRIVED", "STARTED", "REACHED"].includes(
                    booking.status
                )
            ) || []
        );
    }, [activeBookings]);

    // 2. Active drivers (available drivers from location service) - memoized
    const activeDrivers = useMemo(() => {
        return (
            locations
                ?.filter((driver) => {
                    // Only show drivers that are NOT in ongoing trips when not in highlight mode
                    const hasOngoingTrip = ongoingBookings.some(
                        (booking) => booking.driver === driver.id
                    );
                    return !hasOngoingTrip; // Only available drivers
                })
                .map((driver) => {
                    return {
                        ...driver,
                        carImage: "/taxi-stop.png", // Available driver icon (gray/default)
                        tripStatus: "available",
                        type: "driver", // Mark as driver marker
                        bookingRef: null,
                        booking: null,
                        customerName: null,
                        bookingReference: null,
                        pickupAddress: null,
                        dropAddress: null,
                    };
                }) || []
        );
    }, [locations, ongoingBookings]);

    // 3. Ongoing trip markers (from booking data with real-time tracking)
    const ongoingTripMarkers = ongoingBookings.map((booking) => {
        // Priority for coordinates:
        // 1. Real-time tracking location (Firebase tracking/{bookingId})
        // 2. Driver's current location (allLocationsRef)
        // 3. Last trip coordinate from booking
        // 4. Pickup location fallback

        let lat, lng;
        let hasRealTimeTracking = false;

        // Check if we have real-time tracking data for this specific booking
        const realtimeLocation = realTimeLocations.get(booking.id);
        if (realtimeLocation) {
            // Use real-time tracking location (most accurate for ongoing trips)
            lat = realtimeLocation.lat;
            lng = realtimeLocation.lng;
            hasRealTimeTracking = true;
        } else {
            // Use driver's general location from allLocationsRef as fallback
            const driverLocation = locations?.find(
                (driver) => driver.id === booking.driver
            );

            if (driverLocation) {
                lat = driverLocation.lat;
                lng = driverLocation.lng;
            } else if (booking.coords && booking.coords.length > 0) {
                // Use last coordinate from trip
                const lastCoord = booking.coords[booking.coords.length - 1];
                lat = lastCoord.latitude;
                lng = lastCoord.longitude;
            } else {
                // Fallback to pickup location
                lat = booking.pickup?.lat;
                lng = booking.pickup?.lng;
            }
        }

        // Get real-time status if available, otherwise use booking status
        const realTimeStatus = realTimeBookingStatuses.get(booking.id);
        const currentStatus = realTimeStatus?.status || booking.status;
        const hasRealTimeStatus = realTimeStatus !== undefined;

        return {
            id: `trip-${booking.id}`,
            lat: lat,
            lng: lng,
            drivername: booking.driver_name,
            carnumber: booking.vehicleNumber || booking.vehicle_number,
            cartype: booking.carType,
            carImage: highlightOngoingTrips
                ? "/taxi-ongoing.png"
                : "/taxi-stop.png",
            tripStatus: currentStatus, // Use real-time status if available
            originalStatus: booking.status, // Keep original status for comparison
            bookingRef: booking.id,
            booking: booking,
            customerName: booking.customer_name,
            bookingReference: booking.reference,
            pickupAddress: booking.pickupAddress,
            dropAddress: booking.dropAddress,
            type: "trip", // Mark as trip marker
            phone: booking.driver_contact,
            rating: booking.driverRating,
            hasRealTimeTracking: hasRealTimeTracking, // Indicator for real-time tracking
            hasRealTimeStatus: hasRealTimeStatus, // Indicator for real-time status
            realTimeStatusData: realTimeStatus, // Full real-time status data
        };
    });

    // Combine markers based on highlight mode - memoized to prevent re-renders
    const allMarkers = useMemo(() => {
        return highlightOngoingTrips
            ? ongoingTripMarkers // Only show ongoing trips when highlighted
            : [...activeDrivers, ...ongoingTripMarkers]; // Show both when not highlighted
    }, [highlightOngoingTrips, ongoingTripMarkers, activeDrivers]);

    // Calculate ongoing trips count
    const ongoingTripsCount = ongoingBookings.length;

    // Handle marker click
    const handleMarkerClick = (driver) => {
        setSelectedDriver(driver);
    };

    // Memoized function to get updated selected driver with real-time data
    const updatedSelectedDriver = useMemo(() => {
        if (!selectedDriver) return null;

        // Find current marker data for the selected driver
        const currentMarker = allMarkers.find(
            (marker) =>
                marker.id === selectedDriver.id ||
                marker.bookingRef === selectedDriver.bookingRef
        );

        if (currentMarker) {
            // Get real-time status and location for this booking
            const bookingId =
                selectedDriver.bookingRef || currentMarker.bookingRef;
            const realTimeStatus = bookingId
                ? realTimeBookingStatuses.get(bookingId)
                : null;
            const realTimeLocation = bookingId
                ? realTimeLocations.get(bookingId)
                : null;

            // Return updated driver data with real-time information
            return {
                ...selectedDriver,
                ...currentMarker,
                // Override with real-time data if available
                ...(realTimeStatus && {
                    tripStatus: realTimeStatus.status,
                    hasRealTimeStatus: true,
                    realTimeStatusData: realTimeStatus,
                }),
                ...(realTimeLocation && {
                    lat: realTimeLocation.lat,
                    lng: realTimeLocation.lng,
                    hasRealTimeTracking: true,
                }),
                // Ensure we preserve the original booking reference
                bookingRef:
                    selectedDriver.bookingRef || currentMarker.bookingRef,
                // Add timestamp for debugging
                lastUpdated: Date.now(),
            };
        }

        return selectedDriver;
    }, [
        selectedDriver,
        allMarkers,
        realTimeBookingStatuses,
        realTimeLocations,
    ]);

    // Auto-update selected driver when real-time data changes - IMPROVED VERSION
    useEffect(() => {
        if (!selectedDriver || !selectedDriver.bookingRef) {
            return;
        }

        const bookingId = selectedDriver.bookingRef;

        // Check if we have real-time data for this booking
        const hasStatusUpdate = realTimeBookingStatuses.has(bookingId);
        const hasLocationUpdate = realTimeLocations.has(bookingId);

        if (hasStatusUpdate || hasLocationUpdate) {
            // Get real-time data
            const realTimeStatus = realTimeBookingStatuses.get(bookingId);
            const realTimeLocation = realTimeLocations.get(bookingId);

            // Build updates object
            const updates = {};
            let hasChanges = false;

            // Check for status changes
            if (
                realTimeStatus &&
                realTimeStatus.status !== selectedDriver.tripStatus
            ) {
                updates.tripStatus = realTimeStatus.status;
                updates.hasRealTimeStatus = true;
                updates.realTimeStatusData = realTimeStatus;
                hasChanges = true;
            }

            // Check for location changes
            if (realTimeLocation) {
                const locationChanged =
                    selectedDriver.lat !== realTimeLocation.lat ||
                    selectedDriver.lng !== realTimeLocation.lng;

                if (locationChanged) {
                    updates.lat = realTimeLocation.lat;
                    updates.lng = realTimeLocation.lng;
                    updates.hasRealTimeTracking = true;
                    hasChanges = true;
                }
            }

            // Apply updates if there are changes
            if (hasChanges) {
                setSelectedDriver((prevSelected) => {
                    const newSelected = {
                        ...prevSelected,
                        ...updates,
                        lastUpdated: Date.now(),
                    };
                    return newSelected;
                });
            }
        }
    }, [selectedDriver, realTimeBookingStatuses, realTimeLocations]);

    // Toggle fullscreen
    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    // Toggle highlight ongoing trips - dengan notifikasi
    const toggleHighlightOngoingTrips = useCallback(() => {
        const newState = !highlightOngoingTrips;
        setHighlightOngoingTrips(newState);

        // Show notification berdasarkan state
        if (newState) {
            if (ongoingTripsCount > 0) {
                showNotification(
                    t("realtime_tracking_started", {
                        count: ongoingTripsCount,
                    }),
                    "info"
                );
            } else {
                showNotification(t("no_ongoing_trips"), "warning");
            }
        } else {
            showNotification(t("realtime_tracking_stopped"), "info");
        }
    }, [highlightOngoingTrips, ongoingTripsCount, showNotification, t]);

    // Toggle chart visibility
    const toggleChart = () => {
        setShowChart(!showChart);
    };

    // Handle trip details navigation
    const handleTripDetailsOpen = async (driver) => {
        const bookingData = await dispatch(getBookingData(driver.bookingRef));

        navigate("/trip-details", { state: { tripData: bookingData } });
    };

    if (!isLoaded) {
        return (
            <Box
                sx={{
                    height: "calc(100vh - 60px)",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default",
                }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        {t("loading_map")}
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Map loaded successfully

    return (
        <Box>
            {/* Add CSS animations for pulse effect */}
            <style>
                {`
                    @keyframes pulse {
                        0% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.5;
                        }
                        100% {
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <Box
                sx={{
                    height: fullscreen ? "100vh" : "calc(100vh - 60px)",
                    width: "100%",
                    mx: fullscreen ? "auto" : "0px",
                    mt: fullscreen ? "auto" : "0px",
                    position: fullscreen ? "fixed" : "relative",
                    top: fullscreen ? 0 : "auto",
                    left: fullscreen ? 0 : "auto",
                    zIndex: fullscreen ? 9999 : "auto",
                    bgcolor: "background.default",
                    overflow: "hidden",
                    borderRadius: 3,
                }}
            >
                {/* Loading Overlay untuk initial map load */}
                {isMapLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 2000,
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box sx={{ textAlign: "center" }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                {t("loading_map")}...
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Full Background Map */}
                <GoogleMap
                    mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 1,
                    }}
                    center={mapCenter}
                    zoom={mapZoom}
                    onLoad={() => setIsMapLoading(false)}
                    options={{
                        disableDefaultUI: true,
                        zoomControl: false,
                        streetViewControl: false,
                        rotateControl: false,
                        fullscreenControl: false,
                        styles: [
                            {
                                featureType: "poi",
                                elementType: "labels",
                                stylers: [{ visibility: "off" }],
                            },
                            {
                                featureType: "water",
                                elementType: "geometry",
                                stylers: [{ color: "#e0e0e0" }], // darker gray
                            },
                            {
                                featureType: "landscape",
                                elementType: "geometry",
                                stylers: [{ color: "#fafafa" }], // near-white
                            },
                            {
                                featureType: "road",
                                elementType: "geometry",
                                stylers: [{ color: "#e0e0e0" }], // medium light gray
                            },
                            {
                                featureType: "administrative",
                                elementType: "geometry",
                                stylers: [{ color: "#f0f0f0" }], // soft light gray
                            },
                            {
                                featureType: "transit",
                                elementType: "geometry",
                                stylers: [{ color: "#ebebeb" }], // gentle gray
                            },
                            {
                                featureType: "all",
                                elementType: "labels.text.fill",
                                stylers: [{ color: "#555555" }], // muted dark gray
                            },
                            {
                                featureType: "all",
                                elementType: "labels.text.stroke",
                                stylers: [{ color: "#ffffff" }, { weight: 2 }],
                            },
                        ],
                    }}
                >
                    {/* Driver & Trip Markers */}
                    {allMarkers
                        .filter((marker) => {
                            // Validate coordinates first
                            const hasValidCoords =
                                marker.lat &&
                                marker.lng &&
                                typeof marker.lat === "number" &&
                                typeof marker.lng === "number" &&
                                !isNaN(marker.lat) &&
                                !isNaN(marker.lng);

                            if (!hasValidCoords) {
                                return false;
                            }

                            // Since we already filtered in allMarkers logic, show all valid markers
                            return true;
                        })
                        .map((marker, index) => {
                            const isTrip = marker.type === "trip";
                            const isHighlighted =
                                highlightOngoingTrips && isTrip;

                            // Determine marker size and color based on type
                            const markerSize = isHighlighted
                                ? 50
                                : isTrip
                                ? 45
                                : 40;
                            const markerAnchor = isHighlighted
                                ? 25
                                : isTrip
                                ? 22.5
                                : 20;

                            return (
                                <Marker
                                    key={`${marker.type}-${marker.id}-${index}`}
                                    position={{
                                        lat: marker.lat,
                                        lng: marker.lng,
                                    }}
                                    title={`${marker.drivername} - ${
                                        marker.tripStatus
                                    }${
                                        isTrip
                                            ? ` (${t("trip")}: ${
                                                  marker.bookingReference
                                              })`
                                            : ` (${t("available")})`
                                    }${
                                        marker.hasRealTimeTracking
                                            ? ` [${t("realtime_location")}]`
                                            : ""
                                    }${
                                        marker.hasRealTimeStatus
                                            ? ` [${t("realtime_status")}]`
                                            : ""
                                    }${
                                        isHighlighted
                                            ? ` (${t("highlighted")})`
                                            : ""
                                    }`}
                                    onClick={() => handleMarkerClick(marker)}
                                    // animation={
                                    //     isHighlighted
                                    //         ? window.google.maps.Animation
                                    //               .BOUNCE
                                    //         : isTrip
                                    //         ? window.google.maps.Animation.DROP
                                    //         : null
                                    // }
                                    zIndex={
                                        isHighlighted
                                            ? 1000
                                            : isTrip
                                            ? 500
                                            : 100
                                    } // Higher priority for trips and highlighted
                                    icon={
                                        marker.carImage
                                            ? {
                                                  url: marker.carImage,
                                                  scaledSize:
                                                      new window.google.maps.Size(
                                                          markerSize,
                                                          markerSize
                                                      ),
                                                  anchor: new window.google.maps.Point(
                                                      markerAnchor,
                                                      markerSize
                                                  ),
                                              }
                                            : undefined // Use default marker if no custom icon
                                    }
                                />
                            );
                        })}
                </GoogleMap>

                {/* Custom Zoom Controls - Top Left */}
                <Paper
                    elevation={2}
                    sx={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        zIndex: 1000,
                        backgroundColor: "white",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {/* Zoom In Button */}
                        <Tooltip title={t("zoom_in")}>
                            <IconButton
                                onClick={handleZoomIn}
                                disabled={mapZoom >= 20}
                                sx={{
                                    borderRadius: 0,
                                    borderBottom: "1px solid #e0e0e0",
                                    color: mapZoom >= 20 ? "#ccc" : "#333",
                                    backgroundColor: "white",
                                    width: 40,
                                    height: 40,
                                    "&:hover": {
                                        backgroundColor:
                                            mapZoom >= 20 ? "white" : "#f5f5f5",
                                    },
                                }}
                            >
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {/* Zoom Out Button */}
                        <Tooltip title={t("zoom_out")}>
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={mapZoom <= 1}
                                sx={{
                                    borderRadius: 0,
                                    borderBottom: "1px solid #e0e0e0",
                                    color: mapZoom <= 1 ? "#ccc" : "#333",
                                    backgroundColor: "white",
                                    width: 40,
                                    height: 40,
                                    "&:hover": {
                                        backgroundColor:
                                            mapZoom <= 1 ? "white" : "#f5f5f5",
                                    },
                                }}
                            >
                                <ZoomOutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {/* Get Current Location Button */}
                        <Tooltip
                            title={
                                isGettingLocation
                                    ? t("getting_location")
                                    : t("get_current_location")
                            }
                        >
                            <IconButton
                                onClick={handleGetCurrentLocation}
                                disabled={isGettingLocation}
                                sx={{
                                    borderRadius: 0,
                                    color: isGettingLocation ? "#ccc" : "#333",
                                    backgroundColor: "white",
                                    width: 40,
                                    height: 40,
                                    "&:hover": {
                                        backgroundColor: isGettingLocation
                                            ? "white"
                                            : "#f5f5f5",
                                    },
                                }}
                            >
                                {isGettingLocation ? (
                                    <CircularProgress
                                        size={16}
                                        sx={{ color: "#999" }}
                                    />
                                ) : (
                                    <MyLocationIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Paper>

                {/* Statistics Overlay - Top Right - Masonry Layout */}
                <Paper
                    elevation={0}
                    sx={{
                        position: "absolute",
                        top: 20,
                        right: 20,
                        zIndex: 1000,
                        backgroundColor: "transparent",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "flex-end",
                            gap: 2,
                            width: "100%",
                            mb: 2,
                        }}
                    >
                        {/* Baris Pertama - 2 Kartu */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 2,
                                width: "100%",
                            }}
                        >
                            {/* Pasajeros Card */}
                            <Card
                                sx={{
                                    bgcolor: "white",
                                    paddingBlock: 3,
                                    color: "gray",
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    gap: 2,
                                    borderRadius: 3,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow:
                                            "0 4px 16px rgba(0,0,0,0.12)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <svg
                                    style={{
                                        color: "gray",
                                    }}
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_3111_32738)">
                                        <path
                                            d="M18.0001 7.16C17.9401 7.15 17.8701 7.15 17.8101 7.16C16.4301 7.11 15.3301 5.98 15.3301 4.58C15.3301 3.15 16.4801 2 17.9101 2C19.3401 2 20.4901 3.16 20.4901 4.58C20.4801 5.98 19.3801 7.11 18.0001 7.16Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M16.9699 14.4402C18.3399 14.6702 19.8499 14.4302 20.9099 13.7202C22.3199 12.7802 22.3199 11.2402 20.9099 10.3002C19.8399 9.59016 18.3099 9.35016 16.9399 9.59016"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M5.96998 7.16C6.02998 7.15 6.09998 7.15 6.15998 7.16C7.53998 7.11 8.63998 5.98 8.63998 4.58C8.63998 3.15 7.48998 2 6.05998 2C4.62998 2 3.47998 3.16 3.47998 4.58C3.48998 5.98 4.58998 7.11 5.96998 7.16Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M6.99994 14.4402C5.62994 14.6702 4.11994 14.4302 3.05994 13.7202C1.64994 12.7802 1.64994 11.2402 3.05994 10.3002C4.12994 9.59016 5.65994 9.35016 7.02994 9.59016"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M12.0001 14.6297C11.9401 14.6197 11.8701 14.6197 11.8101 14.6297C10.4301 14.5797 9.33008 13.4497 9.33008 12.0497C9.33008 10.6197 10.4801 9.46973 11.9101 9.46973C13.3401 9.46973 14.4901 10.6297 14.4901 12.0497C14.4801 13.4497 13.3801 14.5897 12.0001 14.6297Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M9.09021 17.7794C7.68021 18.7194 7.68021 20.2594 9.09021 21.1994C10.6902 22.2694 13.3102 22.2694 14.9102 21.1994C16.3202 20.2594 16.3202 18.7194 14.9102 17.7794C13.3202 16.7194 10.6902 16.7194 9.09021 17.7794Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3111_32738">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        sx={{
                                            lineHeight: 1,
                                            color: "#333",
                                        }}
                                    >
                                        {customerCount}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#666",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {t("total_cumtomer")}
                                    </Typography>
                                </Box>
                            </Card>

                            {/* Conductores Card */}
                            <Card
                                sx={{
                                    bgcolor: "white",
                                    paddingBlock: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    gap: 2,
                                    borderRadius: 3,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow:
                                            "0 4px 16px rgba(0,0,0,0.12)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_3111_32754)">
                                        <path
                                            d="M9.16006 10.87C9.06006 10.86 8.94006 10.86 8.83006 10.87C6.45006 10.79 4.56006 8.84 4.56006 6.44C4.56006 3.99 6.54006 2 9.00006 2C11.4501 2 13.4401 3.99 13.4401 6.44C13.4301 8.84 11.5401 10.79 9.16006 10.87Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M16.4098 4C18.3498 4 19.9098 5.57 19.9098 7.5C19.9098 9.39 18.4098 10.93 16.5398 11C16.4598 10.99 16.3698 10.99 16.2798 11"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M4.16021 14.56C1.74021 16.18 1.74021 18.82 4.16021 20.43C6.91021 22.27 11.4202 22.27 14.1702 20.43C16.5902 18.81 16.5902 16.17 14.1702 14.56C11.4302 12.73 6.92021 12.73 4.16021 14.56Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M18.3398 20C19.0598 19.85 19.7398 19.56 20.2998 19.13C21.8598 17.96 21.8598 16.03 20.2998 14.86C19.7498 14.44 19.0798 14.16 18.3698 14"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3111_32754">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        sx={{
                                            lineHeight: 1,
                                            color: "#333",
                                        }}
                                    >
                                        {driverCount}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#666",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {t("total_drivers")}
                                    </Typography>
                                </Box>
                            </Card>

                            {/* Active Drivers Card */}
                            <Card
                                sx={{
                                    bgcolor: "white",
                                    display: "flex",
                                    paddingBlock: 3,
                                    alignItems: "center",
                                    px: 2,
                                    gap: 2,
                                    borderRadius: 3,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow:
                                            "0 4px 16px rgba(0,0,0,0.12)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_3111_32756)">
                                        <path
                                            d="M12.1601 10.87C12.0601 10.86 11.9401 10.86 11.8301 10.87C9.45006 10.79 7.56006 8.84 7.56006 6.44C7.56006 3.99 9.54006 2 12.0001 2C14.4501 2 16.4401 3.99 16.4401 6.44C16.4301 8.84 14.5401 10.79 12.1601 10.87Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M7.16021 14.56C4.74021 16.18 4.74021 18.82 7.16021 20.43C9.91021 22.27 14.4202 22.27 17.1702 20.43C19.5902 18.81 19.5902 16.17 17.1702 14.56C14.4302 12.73 9.92021 12.73 7.16021 14.56Z"
                                            stroke="gray"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3111_32756">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        sx={{
                                            lineHeight: 1,
                                            color: "#333",
                                        }}
                                    >
                                        {activeCount}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#666",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {t("active_driver")}
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>

                        {/* Baris Kedua - 2 Kartu */}
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-end",
                            }}
                        >
                            {/* Viajes en curso Card */}
                            <Card
                                onClick={toggleHighlightOngoingTrips}
                                sx={{
                                    bgcolor: highlightOngoingTrips
                                        ? "#e3f2fd"
                                        : "white",
                                    paddingBlock: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "50%",
                                    px: 2,
                                    gap: 2,
                                    borderRadius: 3,
                                    boxShadow: highlightOngoingTrips
                                        ? "0 4px 16px rgba(33, 150, 243, 0.3)"
                                        : "0 2px 8px rgba(0,0,0,0.08)",
                                    transition: "all 0.2s ease-in-out",
                                    cursor: "pointer",
                                    border: highlightOngoingTrips
                                        ? "2px solid #2196F3"
                                        : "none",
                                    "&:hover": {
                                        boxShadow: highlightOngoingTrips
                                            ? "0 6px 20px rgba(33, 150, 243, 0.4)"
                                            : "0 4px 16px rgba(0,0,0,0.12)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <Box
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "start",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: highlightOngoingTrips
                                                    ? "#1976D2"
                                                    : "#666",
                                                fontSize: "1.2rem",
                                                fontWeight:
                                                    highlightOngoingTrips
                                                        ? "bold"
                                                        : "normal",
                                            }}
                                        >
                                            {t("ongoing_trips")}
                                        </Typography>
                                        {highlightOngoingTrips &&
                                            ongoingTripsCount > 0 && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.3,
                                                        backgroundColor:
                                                            "#e8f5e8",
                                                        borderRadius: "12px",
                                                        px: 0.8,
                                                        py: 0.3,
                                                        border: "1px solid #4CAF50",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                                "#4CAF50",
                                                            animation:
                                                                "pulse 2s infinite",
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "0.6rem",
                                                            color: "#2E7D32",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {t("live")}
                                                    </Typography>
                                                </Box>
                                            )}
                                    </Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        textAlign="center"
                                        sx={{
                                            lineHeight: 1,
                                            color: highlightOngoingTrips
                                                ? "#1976D2"
                                                : "#333",
                                        }}
                                    >
                                        {ongoingTripsCount}
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>

                        {/* Baris Ketiga - PowerTranz Card (Conditional) */}
                        {/* {powerTranzActive && auth?.profile?.usertype === "admin" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Card
                sx={{
                  bgcolor: "white",
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  borderRadius: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  width: "50%",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() =>
                  (window.location.href = "/powertranz-transactions")
                }
              >
                <PaymentIcon
                  sx={{
                    fontSize: 28,
                    color: "#9C27B0",
                    mr: 1.5,
                  }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: "1rem",
                      lineHeight: 1,
                      color: "#333",
                    }}
                  >
                    PowerTranz
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "0.8rem",
                    }}
                  >
                    Transactions
                  </Typography>
                </Box>
              </Card>
            </Box>
          )} */}
                    </Box>

                    {/* Gross Earning Section - Hidden */}
                    <Box
                        sx={{
                            mt: 3,
                            mb: 3,
                            px: 2,
                            display: "none",
                        }}
                    >
                        {/* Section Title and Country Selector */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#333",
                                }}
                            >
                                {t("gross_earning") || "Gross Earning"}
                            </Typography>

                            <FormControl
                                sx={{
                                    minWidth: 200,
                                    bgcolor: "white",
                                    borderRadius: 1,
                                }}
                            >
                                <Select
                                    value={selectedCountry}
                                    onChange={handleCountrySelect}
                                    displayEmpty
                                    size="small"
                                    sx={{
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>{t("select_country")}</em>
                                    </MenuItem>
                                    {countries &&
                                        countries.map((country) => (
                                            <MenuItem
                                                key={country.label}
                                                value={country.value}
                                            >
                                                {country.label}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Gross Earning Cards */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            {/* Today Earning */}
                            <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
                                <DashboardCard
                                    title={t("today_text")}
                                    subTitle=""
                                    value={dailygross}
                                    color={colors.GREEN}
                                    background="linear-gradient(135deg, #4CAF50 0%, #45a049 100%)"
                                    symbol={data?.swipe_symbol}
                                />
                            </Box>

                            {/* This Month Earning */}
                            <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
                                <DashboardCard
                                    title={t("this_month_text")}
                                    subTitle=""
                                    value={monthlygross}
                                    color="#2196F3"
                                    background="linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
                                    symbol={data?.swipe_symbol}
                                />
                            </Box>

                            {/* Total Earning */}
                            <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
                                <DashboardCard
                                    title={t("total")}
                                    subTitle=""
                                    value={totalgross}
                                    color="#9C27B0"
                                    background="linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
                                    symbol={data?.swipe_symbol}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Control Panel - Simplified */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                            mt: 2,
                        }}
                    >
                        <Tooltip
                            title={
                                showChart ? t("hide_chart") : t("show_chart")
                            }
                        >
                            <IconButton
                                onClick={toggleChart}
                                sx={{
                                    bgcolor: "rgba(0,0,0,0.1)",
                                    color: "white",
                                }}
                            >
                                {showChart ? (
                                    <VisibilityOffIcon />
                                ) : (
                                    <BarChartIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title={
                                fullscreen
                                    ? t("exit_fullscreen")
                                    : t("fullscreen")
                            }
                        >
                            <IconButton
                                onClick={toggleFullscreen}
                                sx={{
                                    bgcolor: "rgba(0,0,0,0.1)",
                                    color: "white",
                                }}
                            >
                                {fullscreen ? (
                                    <CloseIcon />
                                ) : (
                                    <FullscreenIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Paper>

                {/* Driver Details Sidebar */}
                {updatedSelectedDriver && (
                    <Paper
                        elevation={0}
                        sx={{
                            position: "absolute",
                            top: 100, // Moved down to avoid overlap with zoom controls
                            left: 20,
                            zIndex: 1000,
                            bgcolor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(10px)",
                            borderRadius: 2,
                            p: 1.5,
                            minWidth: 280,
                            maxWidth: 320,
                            maxHeight: "75vh",
                            overflow: "auto",
                            border: "1px solid rgba(0, 0, 0, 0.1)",
                            fontSize: "0.875rem",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{ fontSize: "1rem" }}
                                >
                                    {t("driver_details")}
                                </Typography>
                                {(updatedSelectedDriver.hasRealTimeStatus ||
                                    updatedSelectedDriver.hasRealTimeTracking) && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.3,
                                            backgroundColor: "#e8f5e8",
                                            borderRadius: "12px",
                                            px: 0.8,
                                            py: 0.3,
                                            border: "1px solid #4CAF50",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                backgroundColor: "#4CAF50",
                                                animation: "pulse 2s infinite",
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: "0.6rem",
                                                color: "#2E7D32",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {t("live")}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <IconButton
                                onClick={() => setSelectedDriver(null)}
                                size="small"
                                sx={{ p: 0.5 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* 2 Column Layout */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            {/* Column 1 */}
                            <Box>
                                {/* Name */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("name")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "text.primary",
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {updatedSelectedDriver.drivername}
                                    </Typography>
                                </Box>

                                {/* Car Type */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("car_type")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "text.primary",
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {updatedSelectedDriver.cartype ||
                                            updatedSelectedDriver.carType ||
                                            t("not_available")}
                                    </Typography>
                                </Box>

                                {/* Phone */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("phone")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "text.primary",
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {updatedSelectedDriver.phone}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Column 2 */}
                            <Box>
                                {/* Vehicle Number */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("vehicle_number")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "text.primary",
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {updatedSelectedDriver.carnumber ||
                                            updatedSelectedDriver.carNumber ||
                                            t("not_available")}
                                    </Typography>
                                </Box>

                                {/* Rating */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("rating")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "text.primary",
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {updatedSelectedDriver.rating ||
                                            t("not_available")}
                                    </Typography>
                                </Box>

                                {/* Status */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            mb: 0.2,
                                        }}
                                    >
                                        {t("status")}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        <Chip
                                            label={
                                                updatedSelectedDriver.tripStatus ===
                                                "available"
                                                    ? t("available")
                                                    : t(
                                                          updatedSelectedDriver.tripStatus
                                                      )
                                            }
                                            color={
                                                updatedSelectedDriver.tripStatus ===
                                                "available"
                                                    ? "success"
                                                    : updatedSelectedDriver.tripStatus ===
                                                      "STARTED"
                                                    ? "warning"
                                                    : updatedSelectedDriver.tripStatus ===
                                                      "ACCEPTED"
                                                    ? "info"
                                                    : updatedSelectedDriver.tripStatus ===
                                                      "REACHED"
                                                    ? "primary"
                                                    : updatedSelectedDriver.tripStatus ===
                                                      "PAID"
                                                    ? "secondary"
                                                    : "default"
                                            }
                                            size="small"
                                            sx={{
                                                fontSize: "0.7rem",
                                                height: 18,
                                                // Add visual indicator for real-time status
                                                ...(updatedSelectedDriver.hasRealTimeStatus && {
                                                    border: "2px solid #4CAF50",
                                                    animation:
                                                        "pulse 2s infinite",
                                                }),
                                            }}
                                        />
                                    </Box>
                                    {updatedSelectedDriver.booking && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: "0.65rem",
                                                display: "block",
                                                mt: 0.3,
                                            }}
                                        >
                                            ID:{" "}
                                            {updatedSelectedDriver.bookingRef}
                                        </Typography>
                                    )}
                                    {updatedSelectedDriver.realTimeStatusData && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: "0.6rem",
                                                display: "block",
                                                mt: 0.2,
                                            }}
                                        >
                                            {t("last_update")}:{" "}
                                            {new Date(
                                                updatedSelectedDriver.realTimeStatusData.timestamp
                                            ).toLocaleTimeString()}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Additional booking details for ongoing trips */}
                        {updatedSelectedDriver.tripStatus !== "available" &&
                            updatedSelectedDriver.booking && (
                                <>
                                    <Divider sx={{ mb: 1, mt: 0.5 }} />

                                    {/* Trip Details in 2 columns */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        {/* Customer */}
                                        <Box>
                                            <Box sx={{ mb: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        fontWeight: 500,
                                                        color: "text.secondary",
                                                        mb: 0.2,
                                                    }}
                                                >
                                                    {t("customer")}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.8rem",
                                                        color: "text.primary",
                                                        fontWeight: 500,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {updatedSelectedDriver.customerName ||
                                                        t("not_available")}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Booking Ref */}
                                        <Box>
                                            <Box sx={{ mb: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        fontWeight: 500,
                                                        color: "text.secondary",
                                                        mb: 0.2,
                                                    }}
                                                >
                                                    {t("booking_ref")}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.8rem",
                                                        color: "text.primary",
                                                        fontWeight: 500,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {updatedSelectedDriver.bookingReference ||
                                                        t("not_available")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Addresses - Full width */}
                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 500,
                                                color: "text.secondary",
                                                mb: 0.2,
                                            }}
                                        >
                                            {t("pickup")}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.75rem",
                                                color: "text.primary",
                                                fontWeight: 400,
                                                lineHeight: 1.2,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {updatedSelectedDriver.pickupAddress ||
                                                t("not_available")}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 500,
                                                color: "text.secondary",
                                                mb: 0.2,
                                            }}
                                        >
                                            {t("drop")}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.75rem",
                                                color: "text.primary",
                                                fontWeight: 400,
                                                lineHeight: 1.2,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {updatedSelectedDriver.dropAddress ||
                                                t("not_available")}
                                        </Typography>
                                    </Box>
                                </>
                            )}

                        {updatedSelectedDriver.tripStatus !== "available" && (
                            <Box
                                sx={{
                                    mt: 1.5,
                                    display: "flex",
                                    gap: 0.8,
                                    flexDirection: "column",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="small"
                                    sx={{
                                        bgcolor: "#2196F3",
                                        "&:hover": { bgcolor: "#1976D2" },
                                        fontSize: "0.75rem",
                                        py: 0.8,
                                    }}
                                    onClick={() =>
                                        handleTripDetailsOpen(
                                            updatedSelectedDriver
                                        )
                                    }
                                >
                                    {t("view_trip_details")}
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    sx={{
                                        borderColor: "#4CAF50",
                                        color: "#4CAF50",
                                        fontSize: "0.75rem",
                                        py: 0.8,
                                        "&:hover": {
                                            borderColor: "#388E3C",
                                            color: "#388E3C",
                                            bgcolor: "rgba(76, 175, 80, 0.04)",
                                        },
                                    }}
                                    onClick={() => {
                                        // Navigate to share trip using booking ID
                                        const bookingId =
                                            updatedSelectedDriver.bookingRef;

                                        window.open(
                                            `/sharetrip/${bookingId}`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    {t("share_trip")}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                )}

                {/* Custom Notification Snackbar */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={handleCloseNotification}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    sx={{ mt: 8 }} // Add margin top to avoid overlap with header
                >
                    <Alert
                        onClose={handleCloseNotification}
                        severity={notification.severity}
                        sx={{
                            width: "100%",
                            maxWidth: 600,
                            fontSize: "0.9rem",
                            "& .MuiAlert-message": {
                                whiteSpace: "pre-line", // Allow line breaks in messages
                            },
                        }}
                        variant="filled"
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box>

            {/* Chart Section - Bottom Overlay */}
            {showChart && (
                <Paper
                    elevation={0}
                    sx={{
                        // position: "absolute",
                        // bottom: 20,
                        // left: 20,
                        // right: 20,
                        // zIndex: 1000,
                        marginBlock: 2,
                        borderRadius: 3,
                        p: 2,
                        height: "280px",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        transition: "all 0.3s ease-in-out",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: "bold",
                                color: "text.primary",
                            }}
                        >
                            {t("booking_chart")}
                        </Typography>
                        <Tooltip title={t("hide_chart")}>
                            <IconButton
                                onClick={toggleChart}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(0,0,0,0.05)",
                                    "&:hover": {
                                        bgcolor: "rgba(0,0,0,0.1)",
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Suspense
                        fallback={
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: 200,
                                }}
                            >
                                <CircularProgress size={32} />
                            </Box>
                        }
                    >
                        <Chart
                            options={barChartOptions}
                            series={barChart}
                            type="area"
                            width="100%"
                            height={200}
                        />
                    </Suspense>
                </Paper>
            )}
        </Box>
    );
};

export default DashboardNewUI;
